import { useTranslate } from '@ur-solutions/react-hooks'
import clamp from 'lodash/clamp'
import range from 'lodash/range'
import type {
  RangeNode,
  TimesDriverActivitiesAggregated,
  TypeChart,
} from '@/modules/activities'
import {
  enforceHourMinuteString,
  getBackgroundColor,
} from '@/modules/activities'
import type {
  DriverActivityDayReportQueryReportData,
  SpecificConditionNode,
} from '@/modules/activities/types.graphql'
import React, {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { isMobile } from 'react-device-detect'
import styled from 'styled-components'
import { ActivityArea } from './ActivityArea'
import type { Activity, Infringement, Issue } from './Schedule.types'
import { ScheduleRange } from './ScheduleRange'
import { LegendItem } from './LegendItem'
import { useGetActivitiesByType } from '../../hooks'
import { getPreferredLanguage } from '../../../../locale/util'
import { addDays, startOfDay, startOfMonth, startOfWeek } from 'date-fns'
import theme from '../../../../theme'
import { v4 as uuid } from 'uuid'

const TOPBAR_HEIGHT = 40
const SIDEBAR_WIDTH = 88
const DATA_ROW_HEIGHT = 245
const DATA_ROW_HEIGHT_MONTH_FULL_PDF = 1000
const DATA_ROW_HEIGHT_PDF = 400
const COMPACT_DATA_ROW_HEIGHT = 200
const OVERVIEW_HEIGHT = 35

const MOBILE_TOPBAR_HEIGHT = 40
const MOBILE_SIDEBAR_WIDTH = 122
const MOBILE_DATA_ROW_HEIGHT = 72

const MIN_RESOLUTION = 3
export const MAX_RESOLUTION = 24
export const MAX_RESOLUTION_WEEK = 720

export const DAY_RESOLUTION = 12
export const WEEK_RESOLUTION = 168
export const MONTH_RESOLUTION = 720

type RangeMode = 'normal' | 'overview'

const Wrapper = styled(ActivityArea)<{
  compact: boolean
  isDayMode: boolean
  isPrint: boolean
  isMonthBigPrint: boolean
}>`
  display: grid;
  grid-template-columns: ${props => (props.isDayMode ? SIDEBAR_WIDTH : 0)}px 1fr;
  grid-template-rows: ${TOPBAR_HEIGHT}px ${props =>
      props.compact
        ? COMPACT_DATA_ROW_HEIGHT
        : props.isPrint
        ? props.isMonthBigPrint
          ? DATA_ROW_HEIGHT_MONTH_FULL_PDF
          : DATA_ROW_HEIGHT_PDF
        : DATA_ROW_HEIGHT}px ${OVERVIEW_HEIGHT}px auto;
  grid-template-areas:
    'sidebar hours'
    'sidebar data'
    'sidebar overview'
    'sidebar legend';
  width: 100%;

  ${props => props.theme.media.mobile} {
    width: auto;
    overflow-x: auto;
    grid-template-columns: ${MOBILE_SIDEBAR_WIDTH}px auto;
    grid-template-rows: ${MOBILE_TOPBAR_HEIGHT}px ${MOBILE_DATA_ROW_HEIGHT}px ${OVERVIEW_HEIGHT}px auto;
  }
`
const Sidebar = styled.div<{ compact: boolean; isDayMode: boolean }>`
  grid-area: sidebar;
  position: relative;
  z-index: 1;

  display: ${props => (props.isDayMode ? 'grid' : 'none')};
  grid-template-rows: ${TOPBAR_HEIGHT}px auto;
  border-right: 4px solid rgba(0, 0, 0, 0.05);
  box-shadow: -4px 4px 16px ${props => props.theme.colors.gray8};

  ${props => props.theme.media.mobile} {
    grid-template-rows: ${MOBILE_TOPBAR_HEIGHT}px auto;
  }
`

const LegendWrapper = styled.div<{ isPrint: boolean; isDayMode: boolean }>`
  grid-area: legend;
  display: flex;
  height: fit-content;
  justify-content: space-between;
  padding: 8px;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: flex-start;

  ${props =>
    props.isPrint &&
    !props.isDayMode &&
    `
      border-left: 2px solid ${props.theme.colors.gray8};
    `}

  ${props =>
    props.isPrint &&
    `
      border-bottom: 2px solid ${props.theme.colors.gray8};
      border-right: 2px solid ${props.theme.colors.gray8};
    `}
`

const LegendInnerWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const BaseWrapper = styled.div<{ isPrint: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  ${props =>
    props.isPrint &&
    `
      border-left: 2px solid ${props.theme.colors.gray8};
      border-bottom: 2px solid ${props.theme.colors.gray8};
    `}
`

interface HoursProps {
  columns: number
  compact: boolean
  isPrint: boolean
}

const Hours = styled.div<HoursProps>`
  grid-area: hours;

  display: grid;
  grid-template-columns: repeat(${props => props.columns}, 1fr);
  grid-template-rows: ${TOPBAR_HEIGHT}px;

  ${props =>
    props.isPrint &&
    `
     border-top: 2px solid ${props.theme.colors.gray8};
     border-right: 2px solid ${props.theme.colors.gray8};
    `}
  ${props =>
    !props.compact &&
    `
      border-top: 1px solid ${props.theme.colors.gray8};
    `}
    & > div {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    font-weight: 600;
    color: ${props => props.theme.colors.gray5};

    &:nth-child(even) {
      background-color: ${props => props.theme.colors.quaternary};
    }
  }

  ${props => props.theme.media.mobile} {
    grid-template-columns: repeat(${props => props.columns}, 100px);
    grid-template-rows: ${MOBILE_TOPBAR_HEIGHT}px;
  }
`

const Data = styled.div<{
  compact: boolean
  isPrint: boolean
  isMonthBigPrint: boolean
}>`
  grid-area: data;

  display: grid;
  grid-template-rows: ${props =>
    props.compact
      ? COMPACT_DATA_ROW_HEIGHT
      : props.isPrint
      ? props.isMonthBigPrint
        ? DATA_ROW_HEIGHT_MONTH_FULL_PDF
        : DATA_ROW_HEIGHT_PDF
      : DATA_ROW_HEIGHT}px;
  overflow: hidden;

  ${props =>
    props.isPrint &&
    `
     border-bottom: 2px solid ${props.theme.colors.gray8};
     border-right: 2px solid ${props.theme.colors.gray8};
    `}
  scroll-behavior: none;

  ${props => props.theme.media.mobile} {
    grid-template-rows: ${MOBILE_DATA_ROW_HEIGHT}px;
  }
`

interface StyledDataRowProps {
  columns: number
}

const StyledDataRow = styled.div<StyledDataRowProps>`
  position: relative;

  display: grid;
  grid-template-columns: repeat(${props => props.columns}, 1fr);
  border-top: 2px solid ${props => props.theme.colors.gray8};
  border-bottom: 2px solid ${props => props.theme.colors.gray8};
  padding-top: 10px;
  & > div:nth-child {
    background: ${props => props.theme.colors.quaternary};
  }
`

const StyledOverviewRow = styled.div<{
  isWeekly?: boolean
}>`
  position: relative;
  display: grid;
  grid-template-columns: repeat(${props => (props.isWeekly ? 168 : 24)}, 1fr);
`

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  opacity: 0.5;
  pointer-events: none;
  z-index: 3;
`

const FullHourListWrapper = styled.div<{
  isPrint: boolean
  isDayMode: boolean
}>`
  margin-top: 10px;
  grid-area: overview;
  display: grid;
  grid-template-rows: 22px;
  overflow: hidden;

  ${props =>
    props.isPrint &&
    !props.isDayMode &&
    `
      border-left: 2px solid ${props.theme.colors.gray8};
    `}

  ${props =>
    props.isPrint &&
    `
     border-right: 2px solid ${props.theme.colors.gray8};
  `}
`

const Label = styled.span`
  position: absolute;
  z-index: 3;
  top: 10px;
`

const BorderBox = styled.div<{ isPrint?: boolean }>`
  border-bottom: 3px solid ${props => props.theme.colors.gray8};
  border-top: 3px solid ${props => props.theme.colors.gray8};

  ${props =>
    props.isPrint &&
    `
      border-left: 2px solid ${props.theme.colors.gray8};
    `}
`

interface DataRowProps {
  numHours: number
  onFirstBoxWidthUpdate?: (width: number) => void
  isDay: boolean
  children: React.ReactNode
}

const DataRow: React.JSX.CFC<DataRowProps> = ({
  children,
  numHours,
  onFirstBoxWidthUpdate,
  isDay,
}) => {
  return (
    <StyledDataRow columns={numHours}>
      {children}
      {Array.from({ length: numHours }).map((_, i) => {
        const style = getBackgroundColor(i, theme, isDay)

        return (
          <div
            key={i}
            ref={node => {
              i === 0 && onFirstBoxWidthUpdate?.(node?.offsetWidth ?? 0)
            }}
            style={style}
          />
        )
      })}
    </StyledDataRow>
  )
}

const OverviewRow: React.FC<{
  children: React.ReactNode
  isWeekly?: boolean
  onClick?: (evt: React.MouseEvent<HTMLDivElement>) => void
}> = ({ children, isWeekly = false, onClick }) => (
  <StyledOverviewRow
    isWeekly={isWeekly}
    onClick={onClick}
    style={{ cursor: onClick ? 'pointer' : 'default' }}
  >
    {children}
    <Overlay />
  </StyledOverviewRow>
)

interface ScheduleProps {
  activities: Activity[]
  issues: Issue[]
  infringements: Infringement[]
  specificConditions: SpecificConditionNode[]
  report: DriverActivityDayReportQueryReportData | null
  times: TimesDriverActivitiesAggregated
  date: Date
  initialResolution?: number
  initialStartHour?: number
  onlyShowEveryOtherHour?: boolean
  compact?: boolean
  infoTooltip?: string[]
  variant?: TypeChart
  isShowLegend?: boolean
  isPrint?: boolean
  customDaysCount?: number
  customStartDate?: Date
  isOverview?: boolean
  customResolution?: number
  isMonthBigPrint?: boolean
  fullWeek?: boolean
}

export const Schedule = React.forwardRef<HTMLDivElement, ScheduleProps>(
  (
    {
      activities,
      issues,
      infringements,
      specificConditions,
      report,
      date,
      initialResolution,
      initialStartHour,
      onlyShowEveryOtherHour,
      compact = false,
      infoTooltip,
      variant = 'day',
      isShowLegend = true,
      isPrint = false,
      customDaysCount = 0,
      customStartDate,
      isOverview,
      customResolution,
      isMonthBigPrint = false,
      times,
      fullWeek = false,
    },
    ref
  ) => {
    const translations = useTranslate({
      issues: 'common.issues',
      events: 'common.events',
      work: 'activities.work',
      rest: 'activities.rest',
      pause: 'common.pause',
      driving: 'activities.driving',
      countries: 'common.countries',
      vehicle: 'common.vehicle',

      lastFetched: 'activities.last-fetched',

      hours: ['common.hours', val => val.toLowerCase()],
      minutes: ['common.minutes', val => val.toLowerCase()],

      nHours: ['common.n-hours', { n: '' }],
      nMinutes: ['common.n-minutes', { n: 0 }],
      nIssues: ['common.n-issues', { n: 0 }],
      nInfringements: ['common.n-infringements', { n: 0 }],
      nSpecificCondition: ['common.n-specific-conditions', { n: 0 }],
      available: 'common.availability',
      unavailable: 'common.unavailable',
      unknown: 'common.unknown',
    })
    const language = getPreferredLanguage()
    const isWeeklyMode = variant === 'week'
    const isShowOverview =
      isOverview !== undefined
        ? isOverview
        : variant === 'week' || variant === 'day'
    const isMonthlyMode = variant === 'month'
    const isDayMode = variant === 'day'

    const dataRef = useRef<HTMLDivElement>(null)
    const [overviewCalendarWidth, setOverviewCalendarWidth] = useState(0)
    const [calendarWidth, setCalendarWidth] = useState(0)
    const [startHour, setStartHour] = useState(initialStartHour ?? 0)
    const [resolution, setResolution] = useState(initialResolution ?? 12)
    const [colom, setColom] = useState(0)
    const dstOffset = report?.daylightSavingsChange?.offset ?? 0
    const dstTimestamp = report?.daylightSavingsChange?.changeTime ?? 0
    const customMonthResolution = customDaysCount * 24

    const maxResolution = isMonthlyMode
      ? startHour && customDaysCount
        ? startHour + customDaysCount * 24
        : customDaysCount
        ? customMonthResolution
        : MAX_RESOLUTION_WEEK
      : MAX_RESOLUTION

    const totalHours = useMemo(() => {
      if (isWeeklyMode) return WEEK_RESOLUTION
      if (isMonthlyMode && customDaysCount) {
        return customMonthResolution
      }
      if (isMonthlyMode) return MONTH_RESOLUTION
      if (customResolution) return customResolution
      return MAX_RESOLUTION
    }, [
      isWeeklyMode,
      isMonthlyMode,
      customMonthResolution,
      customResolution,
      customDaysCount,
    ])

    const data = useGetActivitiesByType(
      activities,
      report,
      customStartDate || date,
      issues,
      infringements,
      specificConditions,
      variant,
      infoTooltip,
      customDaysCount,
      times?.timeWorkTotal
    )

    useEffect(() => {
      if (!isMonthlyMode || !dataRef.current) return

      const columnWidth = 96

      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          const containerWidth = entry.contentRect.width
          const visibleColumns = Math.floor(containerWidth / columnWidth)
          setColom(visibleColumns)
        }
      })

      observer.observe(dataRef.current)

      return () => observer.disconnect()
    }, [isMonthlyMode])

    const handleSetStartHour = useEffectEvent((hour: number) => {
      setStartHour(hour)
    })

    const handleSetResolution = useEffectEvent((res: number) => {
      setResolution(res)
    })

    useEffect(() => {
      handleSetStartHour(initialStartHour ?? 0)

      if (initialResolution) {
        handleSetResolution(initialResolution)
        return
      }

      if (customResolution) {
        handleSetResolution(customResolution)
        return
      }

      if (isMonthlyMode) {
        if (customDaysCount) {
          if (fullWeek) {
            handleSetResolution(WEEK_RESOLUTION)
            return
          }
          handleSetResolution(Math.min(colom * 24, customMonthResolution))
        } else {
          handleSetResolution(WEEK_RESOLUTION)
        }
        return
      }

      if (isWeeklyMode) {
        handleSetResolution(DAY_RESOLUTION)
        return
      }

      if (isDayMode) {
        handleSetResolution(DAY_RESOLUTION)
        return
      }
    }, [
      variant,
      initialResolution,
      initialStartHour,
      customResolution,
      customDaysCount,
      isWeeklyMode,
      isMonthlyMode,
      isDayMode,
      totalHours,
      colom,
      fullWeek,
      customMonthResolution,
    ])

    useLayoutEffect(() => {
      const el = dataRef.current
      if (!el) return

      const preventDefault = (evt: WheelEvent) => evt.preventDefault()

      const updateWidths = () => {
        setCalendarWidth(el.offsetWidth)
        setOverviewCalendarWidth(el.offsetWidth)
      }

      updateWidths()

      if (resolution < maxResolution - dstOffset) {
        el.addEventListener('wheel', preventDefault, { passive: false })
      }

      window.addEventListener('resize', updateWidths)

      return () => {
        el.removeEventListener('wheel', preventDefault)
        window.removeEventListener('resize', updateWidths)
      }
    }, [dstOffset, maxResolution, resolution, variant])

    const hourRange = useMemo(() => {
      return range(startHour, Math.min(startHour + resolution, totalHours))
    }, [startHour, resolution, totalHours])

    const getTimeRangeForMode = (
      variant: TypeChart,
      startHour: number,
      resolution: number,
      totalHours: number,
      dstOffset: number
    ) => {
      switch (variant) {
        case 'month':
          return {
            startTime: 0,
            endTime: totalHours,
            displayStartTime: startHour,
            displayEndTime: startHour + resolution - dstOffset,
          }
        case 'week':
        case 'day':
        default:
          return {
            startTime: startHour,
            endTime: startHour + resolution - dstOffset,
            displayStartTime: startHour,
            displayEndTime: startHour + resolution - dstOffset,
          }
      }
    }

    const createScheduleRangeFactory =
      (mode: RangeMode) => (nodes: RangeNode[]) => {
        const isOverview = mode === 'overview'

        let startTimeForRange: number
        let endTimeForRange: number
        let calendarWidthForRange: number
        let resolutionForRange: number

        if (isOverview) {
          startTimeForRange = 0
          endTimeForRange = totalHours
          calendarWidthForRange = overviewCalendarWidth
          resolutionForRange = isWeeklyMode ? 168 : 24
        } else {
          const timeRange = getTimeRangeForMode(
            variant,
            startHour,
            resolution,
            totalHours,
            dstOffset
          )

          if (isMonthlyMode) {
            startTimeForRange = timeRange.displayStartTime
            endTimeForRange = timeRange.displayEndTime
            resolutionForRange = resolution
          } else {
            startTimeForRange = timeRange.startTime
            endTimeForRange = timeRange.endTime
            resolutionForRange = resolution
          }

          calendarWidthForRange = calendarWidth
        }

        return (
          <ScheduleRange
            key={uuid()}
            nodes={nodes}
            startTime={startTimeForRange}
            endTime={endTimeForRange}
            calendarWidth={calendarWidthForRange}
            daylightOffset={report?.daylightSavingsChange?.offset ?? 0}
            variant={variant}
            resolution={resolutionForRange}
            disableTooltip={isOverview}
            disableEvents={isOverview}
            isOverview={isOverview}
          />
        )
      }

    function hourAt(mouseX: number) {
      const mousePercentage = mouseX / calendarWidth
      return Math.floor(resolution * mousePercentage) + startHour
    }

    function zoomData(out: boolean, mouseX?: number) {
      if ((resolution === 3 && !out) || (resolution === 24 && out)) return

      const hourAtMouse =
        typeof mouseX !== 'undefined'
          ? hourAt(mouseX)
          : (startHour + resolution) / 2

      const delta = out ? 1 : -1
      const newResolution = clamp(
        resolution + delta,
        MIN_RESOLUTION,
        maxResolution - dstOffset
      )
      setResolution(newResolution)
      setStartHour(
        clamp(
          Math.floor(hourAtMouse - newResolution / 2),
          0,
          24 - newResolution
        )
      )
    }

    function scrollData(left: boolean) {
      const step = isMonthlyMode ? 24 : 1
      const newStart = clamp(
        startHour + (left ? -step : step),
        0,
        totalHours - resolution
      )
      setStartHour(newStart)
    }

    function handleWheel(evt: React.WheelEvent<HTMLDivElement>) {
      if (
        isMobile ||
        resolution >= maxResolution - dstOffset ||
        evt.deltaY === 0
      )
        return evt
      if (evt.shiftKey)
        zoomData(
          evt.deltaY > 0,
          evt.pageX - evt.currentTarget.getBoundingClientRect().left
        )
      else scrollData(evt.deltaY > 0)
    }

    function hourLabel(hour: number) {
      const hourInDay = hour % 24
      const dayIndex = Math.floor(hour / 24)

      if (isMonthlyMode) {
        const startDate = customStartDate
          ? startOfDay(customStartDate)
          : startOfMonth(date)
        const currentDate = addDays(startDate, dayIndex)

        if (hourInDay === 11) {
          return (
            <Label>{`${currentDate.getDate()}/${
              currentDate.getMonth() + 1
            }`}</Label>
          )
        }
        return ''
      }

      if (isWeeklyMode) {
        const currentDayDate = addDays(
          startOfWeek(date, { weekStartsOn: 1 }),
          dayIndex
        )

        if (hourInDay === 0) {
          return `${currentDayDate.getDate()} ${currentDayDate.toLocaleString(
            language,
            { month: 'short' }
          )}`
        }
        return `${hourInDay.toString().padStart(2, '0')}:00`
      }

      let adjustedHour = hour
      if (dstOffset < 0 && dstTimestamp && hour > dstTimestamp) {
        adjustedHour = hour + dstOffset
      }
      if (dstOffset > 0 && dstTimestamp && hour >= dstTimestamp) {
        adjustedHour = hour + dstOffset
      }

      return `${adjustedHour === 24 ? '00' : adjustedHour}:00`.padStart(5, '0')
    }

    const createScheduleRange = createScheduleRangeFactory('normal')
    const createOverviewScheduleRange = createScheduleRangeFactory('overview')

    function handleOverviewClick(evt: React.MouseEvent<HTMLDivElement>) {
      const overviewElement = evt.currentTarget
      const rect = overviewElement.getBoundingClientRect()
      const clickX = evt.clientX - rect.left
      const clickPercentage = clickX / overviewCalendarWidth
      const clickedHour = Math.floor(totalHours * clickPercentage)

      const newStartHour = Math.max(0, clickedHour - Math.floor(resolution / 2))
      const maxStartHour = totalHours - resolution

      setStartHour(Math.min(newStartHour, maxStartHour))
    }

    return (
      <Wrapper
        $area="schedule"
        compact={compact}
        isDayMode={isDayMode}
        isPrint={isPrint}
        ref={ref}
        isMonthBigPrint={isMonthBigPrint}
      >
        <Sidebar compact={compact} isDayMode={isDayMode}>
          <BorderBox isPrint={isPrint} />
          <BaseWrapper isPrint={isPrint}>
            <span>
              {date.getDate()}{' '}
              {date.toLocaleString(language, { month: 'short' })}
            </span>
          </BaseWrapper>
        </Sidebar>

        <Hours compact={compact} columns={hourRange.length} isPrint={isPrint}>
          {hourRange.map((hour, i) => {
            const style = getBackgroundColor(i, theme, isMonthlyMode)

            return (
              <div style={style} key={hour}>
                <span>
                  {!onlyShowEveryOtherHour || i % 2 === 0
                    ? hourLabel(hour)
                    : ''}
                </span>
              </div>
            )
          })}
        </Hours>

        <Data
          ref={dataRef}
          onWheel={handleWheel}
          compact={compact}
          isPrint={isPrint}
          isMonthBigPrint={isMonthBigPrint}
        >
          <DataRow numHours={hourRange.length} isDay={isMonthlyMode}>
            {data[variant]?.pauseNodes.map(createScheduleRange)}
            {data[variant]?.driveNodes.map(createScheduleRange)}
            {data[variant]?.workNodes.map(createScheduleRange)}
            {data[variant]?.restNodes.map(createScheduleRange)}
            {data[variant]?.availableNodes.map(createScheduleRange)}
            {data[variant]?.vehicleNodes.map(createScheduleRange)}
            {data[variant]?.issueNodes.map(createScheduleRange)}
            {data[variant]?.infringementNodes.map(createScheduleRange)}
            {data[variant]?.specificConditionNodes.map(createScheduleRange)}
          </DataRow>
        </Data>
        <FullHourListWrapper isPrint={isPrint} isDayMode={isDayMode}>
          {isShowOverview && (
            <OverviewRow isWeekly={isWeeklyMode} onClick={handleOverviewClick}>
              {data[variant]?.pauseNodes.map(createOverviewScheduleRange)}
              {data[variant]?.driveNodes.map(createOverviewScheduleRange)}
              {data[variant]?.workNodes.map(createOverviewScheduleRange)}
              {data[variant]?.restNodes.map(createOverviewScheduleRange)}
              {data[variant]?.availableNodes.map(createOverviewScheduleRange)}
              {data[variant]?.vehicleNodes.map(createOverviewScheduleRange)}
              {data[variant]?.issueNodes.map(createOverviewScheduleRange)}
              {data[variant]?.infringementNodes.map(
                createOverviewScheduleRange
              )}
              {data[variant]?.specificConditionNodes.map(
                createOverviewScheduleRange
              )}
            </OverviewRow>
          )}
        </FullHourListWrapper>
        <LegendWrapper isPrint={isPrint} isDayMode={isDayMode}>
          {isShowLegend && (
            <>
              <LegendInnerWrapper>
                <LegendItem
                  color="green600"
                  label={translations.driving}
                  hours={translations.nHours({
                    n: enforceHourMinuteString(times?.timeDriving),
                  })}
                />
                <LegendItem
                  color="orange"
                  label={translations.work}
                  hours={translations.nHours({
                    n: enforceHourMinuteString(times?.timeOtherWork),
                  })}
                />
                <LegendItem
                  color="blue400"
                  label={translations.pause}
                  hours={translations.nHours({
                    n: `${enforceHourMinuteString(times?.timePause)} ${
                      enforceHourMinuteString(times?.timePaidRest) === '00:00'
                        ? ''
                        : '+' + enforceHourMinuteString(times?.timePaidRest)
                    }`,
                  })}
                />
                <LegendItem
                  color="gray7"
                  label={translations.rest}
                  hours={translations.nHours({
                    n: enforceHourMinuteString(times?.timeRest),
                  })}
                />
                <LegendItem
                  color="secondary"
                  label={translations.available}
                  hours={translations.nHours({
                    n: enforceHourMinuteString(times?.timeAvailability),
                  })}
                />
              </LegendInnerWrapper>
              <LegendInnerWrapper>
                <LegendItem
                  color="primary"
                  label={translations.nSpecificCondition({
                    n: specificConditions.length,
                  })}
                  isOtherCondition={true}
                />
                <LegendItem
                  color="red"
                  label={translations.nIssues({ n: issues.length })}
                  isIssue={true}
                />
                <LegendItem
                  color="red"
                  label={translations.nInfringements({
                    n: infringements.length,
                  })}
                  isIssue={true}
                />
              </LegendInnerWrapper>
            </>
          )}
        </LegendWrapper>
      </Wrapper>
    )
  }
)
